/**
 * TPRA Home Dismiss Task Mutation Hook
 * Handles task dismissal with optimistic updates
 */

import { del } from "@/infra/rest";
import { APIEndpoints } from "@/shared/constants/Endpoints";
import { tpraHomeQueryKeys } from "@/shared/constants/QueryKeys";
import { addNotification } from "@/shared/states/notification";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { TasksResponse } from "../types";

/**
 * Dismisses a task by ID
 * @param taskId - The ID of the task to dismiss
 */
async function dismissTaskApi(taskId: string): Promise<void> {
  await del({
    url: `${APIEndpoints.TPRAHomeTasks}/${taskId}`,
  });
}

/**
 * React Query mutation hook for dismissing tasks
 *
 * Features:
 * - Optimistic update: Task removed from UI immediately
 * - Rollback on error: Task reappears if API fails
 * - Error notification: Shows toast on failure
 * - Query invalidation: Ensures data consistency after mutation
 *
 * @returns Mutation object with mutate function and status
 *
 * @example
 * ```typescript
 * function TaskItem({ task }) {
 *   const { mutate: dismissTask, isPending } = useDismissTask();
 *
 *   return (
 *     <button
 *       onClick={() => dismissTask(task.id)}
 *       disabled={isPending}
 *     >
 *       Dismiss
 *     </button>
 *   );
 * }
 * ```
 */
export function useDismissTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: dismissTaskApi,

    onMutate: async (taskId: string) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: tpraHomeQueryKeys.tasks() });

      // Snapshot all matching task queries for rollback
      const previousQueries = queryClient.getQueriesData<TasksResponse>({
        queryKey: tpraHomeQueryKeys.tasks(),
      });

      // Optimistically remove the task from all matching task queries
      queryClient.setQueriesData<TasksResponse>(
        { queryKey: tpraHomeQueryKeys.tasks() },
        (old) =>
          old
            ? {
                ...old,
                tasks: old.tasks.filter((t) => t.id !== taskId),
              }
            : old
      );

      // Return context with snapshot for rollback
      return { previousQueries };
    },

    onError: (_err, _taskId, context) => {
      // Rollback to previous state on error
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }

      // Show error notification
      addNotification({
        type: "error",
        title: "Error",
        message: "Failed to dismiss task. Please try again.",
      });
    },

    onSettled: () => {
      // Always refetch after mutation to ensure consistency
      queryClient.invalidateQueries({ queryKey: tpraHomeQueryKeys.tasks() });
    },
  });
}
